/**
 * Client API — parle au Worker Core.
 *
 * Stratégie offline-first :
 * 1. On écrit TOUJOURS d'abord en local (Dexie).
 * 2. On empile l'opération dans `sync_queue` (outbox).
 * 3. La sync drain la queue quand le réseau revient.
 *
 * Les écritures de lecture (GET) passent en direct avec cache SW en fallback.
 */
import { newId, fcfa } from '@boutikos/domain';
import { db, type VenteRow } from './db';

const CORE_URL = (import.meta.env.VITE_CORE_URL as string | undefined) ?? '';

class TokenStore {
  private token: string | null = null;
  set(t: string) {
    this.token = t;
    localStorage.setItem('boutikos.jwt', t);
  }
  get(): string | null {
    if (this.token) return this.token;
    if (typeof localStorage === 'undefined') return null;
    this.token = localStorage.getItem('boutikos.jwt');
    return this.token;
  }
  clear() {
    this.token = null;
    localStorage.removeItem('boutikos.jwt');
  }
}
export const tokens = new TokenStore();

/** Identité de la boutique courante (cache local après login/inscription). */
export interface Session {
  boutiqueId: string;
  boutiqueNom: string;
  quartier: string | null;
  ville: string | null;
  tel: string;
  userId: string;
  userNom: string;
  role: string;
  permissions: string[];
}
class SessionStore {
  private session: Session | null = null;
  set(s: Session) {
    this.session = s;
    localStorage.setItem('boutikos.session', JSON.stringify(s));
  }
  get(): Session | null {
    if (this.session) return this.session;
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('boutikos.session');
    if (!raw) return null;
    try { this.session = JSON.parse(raw) as Session; return this.session; }
    catch { return null; }
  }
  clear() {
    this.session = null;
    localStorage.removeItem('boutikos.session');
  }
}
export const session = new SessionStore();

async function authHeader(): Promise<Record<string, string>> {
  const t = tokens.get();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

async function requete<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
  };
  const r = await fetch(`${CORE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = (await r.json().catch(() => ({}))) as { data?: T; error?: { code: string; message: string } };
  if (!r.ok) {
    throw new ApiError(
      json.error?.code ?? 'http_error',
      json.error?.message ?? `HTTP ${r.status}`,
      r.status,
    );
  }
  return json.data as T;
}

// ============================================================================
// Auth
// ============================================================================

export interface TokenPair {
  access: string;
  refresh: string;
  expiresIn: number;
}

export async function inscription(input: {
  tel: string;
  nom_boutique?: string;
  nom_user: string;
  pin: string;
  quartier?: string;
}): Promise<{ boutiqueId: string; userId: string; tokens: TokenPair }> {
  const data = await requete<{ boutiqueId: string; userId: string; tokens: TokenPair }>(
    'POST',
    '/auth/inscription',
    input,
  );
  tokens.set(data.tokens.access);
  // Hydrate la session
  await rafraichirSession();
  return data;
}

export async function login(input: { tel: string; pin: string }): Promise<{ tokens: TokenPair }> {
  const data = await requete<{ tokens: TokenPair }>('POST', '/auth/login', input);
  tokens.set(data.tokens.access);
  await rafraichirSession();
  return data;
}

/** Appelle /auth/me et met à jour le SessionStore. Renvoie la session. */
export async function rafraichirSession(): Promise<Session | null> {
  const t = tokens.get();
  if (!t) return null;
  try {
    const s = await requete<Session>('GET', '/auth/me');
    session.set(s);
    return s;
  } catch {
    session.clear();
    return null;
  }
}

// ============================================================================
// Vente — écrit TOUJOURS en local d'abord (offline-first), push outbox ensuite
// ============================================================================

export interface LigneVente {
  articleId: string;
  nom: string;
  prixUnitaire: number;
  qte: number;
  poidsKg?: number;
}

export interface PartPaiement {
  mode: 'cash' | 'momo' | 'orange' | 'moov' | 'credit';
  montant: number;
  momoRef?: string;
}

export async function creerVenteLocale(input: {
  userId: string;
  clientId?: string;
  lignes: LigneVente[];
  paiement: PartPaiement[];
}): Promise<VenteRow> {
  const now = Date.now();
  const total = input.lignes.reduce(
    (s, l) => s + fcfa(fcfa(l.prixUnitaire) * (l.poidsKg ?? l.qte)),
    0,
  );
  const partCredit = input.paiement.filter((p) => p.mode === 'credit').reduce((s, p) => s + p.montant, 0);
  const partCash = input.paiement.filter((p) => p.mode === 'cash').reduce((s, p) => s + p.montant, 0);
  const modes = new Set(input.paiement.map((p) => p.mode));
  const mode = modes.size === 1 ? modes.values().next().value : 'mixte';
  const monnaie = Math.max(0, partCash - total);

  const vente: VenteRow = {
    id: newId(),
    user_id: input.userId,
    clientId: input.clientId,
    lignes: input.lignes,
    paiement: input.paiement,
    total,
    mode,
    monnaie,
    part_credit: partCredit,
    statut: 'valide',
    created_at: now,
    updated_at: now,
    _sync: 'pending',
  };

  await db.transaction('rw', db.ventes, db.articles, db.sync_queue, async () => {
    await db.ventes.add(vente);
    // Décrément stock local
    for (const l of input.lignes) {
      const art = await db.articles.get(l.articleId);
      if (art) {
        const dec = l.poidsKg ?? l.qte;
        await db.articles.update(l.articleId, {
          stock_actuel: Math.max(0, art.stock_actuel - dec),
          updated_at: now,
          _sync: 'pending',
        });
      }
    }
    await db.sync_queue.add({
      table: 'ventes',
      entity_id: vente.id,
      op: 'create',
      payload: vente,
      ts_appareil: now,
      retries: 0,
    });
  });

  return vente;
}

// ============================================================================
// Sync (drain de l'outbox)
// ============================================================================

export async function syncPull(): Promise<{ applied: number }> {
  // POST /sync/push : on envoie tout ce qui est pending
  const pending = await db.sync_queue.toArray();
  if (pending.length === 0) return { applied: 0 };

  const operations = pending.map((p) => ({
    op: p.op,
    table: p.table,
    id: p.entity_id,
    payload: p.payload as Record<string, unknown>,
    ts_appareil: p.ts_appareil,
  }));

  try {
    const result = await requete<{ applied: number; conflicts: unknown[]; new_cursor: number }>(
      'POST',
      '/sync/push',
      { operations, cursor_appareil: 0 },
    );
    // Tout appliqué → on vide la queue
    await db.sync_queue.clear();
    return { applied: result.applied };
  } catch (e) {
    // Réseau KO ou autre : on ne vide pas, on retentera
    for (const op of pending) {
      await db.sync_queue.update(op.id!, { retries: op.retries + 1 });
    }
    throw e;
  }
}

export function enLigne(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

// Auto-sync quand le réseau revient
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    syncPull().catch((e) => console.warn('[sync] push failed:', e));
  });
}
